// src/private/src/pages/app/clients/StaffMeetingRequestsPage.jsx
//
// Staff-only meetings hub. Two jobs:
//   1. Triage open meeting requests from any client — Approve (emails the
//      client a slot-picker link + opens their calendar) or Decline.
//   2. Open a meeting for ANY client outright (the tier/quota bypass), with
//      an option to email them the booking link — even if they never asked.
// Both call the same staff endpoints the per-client schedule panel uses.
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  Divider,
  FormControlLabel,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';

import api from '@src/api/clients';
import {
  approveMeetingRequest,
  declineMeetingRequest,
  fetchAllMeetingRequests,
  issueMeetingGrant,
} from '@src/api/clientSchedule';
import { dayjs } from '@src/corelib';
import { useMessage } from '@src/context/common';
import paths from '@src/paths';


function StaffMeetingRequestsPage() {
  const message = useMessage();
  const [requests, setRequests] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  // Shared grant params (apply to both approve + bypass).
  const [meetings, setMeetings] = useState(1);
  const [days, setDays] = useState(7);

  // Bypass section. Email defaults OFF — staff usually paste the link to the
  // exact person they're already talking to (no recipient guesswork).
  const [selectedClient, setSelectedClient] = useState(null);
  const [bypassNotify, setBypassNotify] = useState(false);
  const [lastOpened, setLastOpened] = useState(null); // { name, url }

  const clampInt = (v, lo, hi, dflt) => {
    const n = parseInt(v, 10);
    if (Number.isNaN(n)) return dflt;
    return Math.max(lo, Math.min(hi, n));
  };
  const grantParams = () => ({
    meetings_allowed: clampInt(meetings, 1, 10, 1),
    expires_in_days: clampInt(days, 1, 90, 7),
  });

  const loadRequests = useCallback(async () => {
    try {
      const res = await fetchAllMeetingRequests();
      setRequests(res.data || []);
    } catch (err) {
      message.error(err?.response?.data?.detail || 'Could not load requests.');
    }
  }, [message]);

  const loadClients = useCallback(async () => {
    try {
      const res = await api.request({
        url: 'dashboard/clients/?fields=id,name,slug,status&page_size=500',
        method: 'GET',
      });
      const list = res.data?.results || res.data || [];
      setClients(
        list
          .filter((c) => Boolean(c.slug))
          .sort((a, b) =>
            (a.name || '').localeCompare(b.name || '', undefined, {
              sensitivity: 'base',
            }),
          ),
      );
    } catch (err) {
      // Soft-fail — the request queue still works without the picker.
    }
  }, []);

  useEffect(() => {
    (async () => {
      setLoading(true);
      await Promise.all([loadRequests(), loadClients()]);
      setLoading(false);
    })();
  }, [loadRequests, loadClients]);

  const run = async (fn, successMsg) => {
    setBusy(true);
    try {
      const res = await fn();
      message.success(
        typeof successMsg === 'function' ? successMsg(res) : successMsg,
      );
      await loadRequests();
    } catch (err) {
      message.error(err?.response?.data?.detail || 'Action failed.');
    } finally {
      setBusy(false);
    }
  };

  const doApprove = (id) =>
    run(
      () => approveMeetingRequest(id, grantParams()),
      'Approved — booking link emailed to the client.',
    );
  const doDecline = (id) =>
    run(() => declineMeetingRequest(id), 'Request declined.');
  const copyLink = (url) => {
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(url).then(
        () => message.success('Link copied.'),
        () => message.error('Could not copy — select the link to copy manually.'),
      );
    } else {
      message.error('Copy not supported here — select the link to copy manually.');
    }
  };

  const doBypass = () => {
    if (!selectedClient?.slug) {
      message.error('Pick a client first.');
      return;
    }
    const client = selectedClient;
    const url = `${window.location.origin}${paths.clientSchedule(client.slug)}`;
    run(
      () =>
        issueMeetingGrant(client.slug, {
          ...grantParams(),
          notify: bypassNotify,
        }),
      (res) => {
        const emailed = res?.data?.notified_emails || [];
        return emailed.length
          ? `Meeting opened for ${client.name} — emailed ${emailed.join(', ')}.`
          : `Meeting opened for ${client.name} — copy the link below to send them.`;
      },
    ).then(() => {
      setLastOpened({ name: client.name, url });
      setSelectedClient(null);
    });
  };

  const newCount = useMemo(
    () => requests.filter((r) => r.status === 'new').length,
    [requests],
  );

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 10 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, maxWidth: 980, mx: 'auto' }}>
      <Typography variant="h4" sx={{ mb: 0.5 }}>
        Meeting Requests
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Approve a client's request (emails them a slot-picker link), or open a
        meeting for any client outright.
      </Typography>

      {/* Shared grant params */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="overline" color="text.secondary">
            Grant settings (applied to approvals & bypasses)
          </Typography>
          <Stack
            direction="row"
            spacing={2}
            alignItems="center"
            flexWrap="wrap"
            useFlexGap
            sx={{ mt: 1 }}
          >
            <TextField
              label="Meetings"
              type="number"
              size="small"
              value={meetings}
              onChange={(e) => setMeetings(e.target.value)}
              inputProps={{ min: 1, max: 10 }}
              sx={{ width: 110 }}
            />
            <TextField
              label="Expires in (days)"
              type="number"
              size="small"
              value={days}
              onChange={(e) => setDays(e.target.value)}
              inputProps={{ min: 1, max: 90 }}
              sx={{ width: 150 }}
            />
          </Stack>
        </CardContent>
      </Card>

      {/* Open a meeting for any client (bypass) */}
      <Card sx={{ mb: 3, borderLeft: 4, borderColor: 'success.main' }}>
        <CardContent>
          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 0.5 }}>
            <LockOpenIcon color="success" fontSize="small" />
            <Typography variant="h6">Open a meeting for a client</Typography>
          </Stack>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Bypass tier/quota so a client can book even if they're Starter or
            out of quota — no request needed. By default we just activate it and
            give you the link to send; tick the box to auto-email their main
            contact instead.
          </Typography>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={2}
            alignItems={{ sm: 'center' }}
          >
            <Autocomplete
              options={clients}
              value={selectedClient}
              onChange={(e, v) => setSelectedClient(v)}
              getOptionLabel={(o) => o?.name || ''}
              isOptionEqualToValue={(o, v) => o.id === v.id}
              sx={{ minWidth: 280, flex: 1 }}
              renderInput={(params) => (
                <TextField {...params} label="Client" size="small" placeholder="Search clients…" />
              )}
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={bypassNotify}
                  onChange={(e) => setBypassNotify(e.target.checked)}
                />
              }
              label="Email their main contact"
            />
            <Button
              variant="contained"
              color="success"
              disabled={busy || !selectedClient}
              startIcon={busy ? <CircularProgress size={16} /> : <LockOpenIcon />}
              onClick={doBypass}
            >
              Open meeting
            </Button>
          </Stack>

          {lastOpened && (
            <Alert
              severity="success"
              sx={{ mt: 2 }}
              action={
                <Button
                  color="inherit"
                  size="small"
                  startIcon={<ContentCopyIcon fontSize="small" />}
                  onClick={() => copyLink(lastOpened.url)}
                >
                  Copy link
                </Button>
              }
            >
              Booking opened for <strong>{lastOpened.name}</strong>. Send them:{' '}
              <Box component="span" sx={{ wordBreak: 'break-all' }}>
                {lastOpened.url}
              </Box>
            </Alert>
          )}
        </CardContent>
      </Card>

      {/* Pending requests queue */}
      <Card>
        <CardContent>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
            <Typography variant="h6">Open requests</Typography>
            <Chip size="small" label={`${requests.length} total`} variant="outlined" />
            {newCount > 0 && (
              <Chip size="small" color="primary" label={`${newCount} new`} />
            )}
          </Stack>

          {requests.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No open meeting requests right now.
            </Typography>
          ) : (
            <Stack divider={<Divider flexItem />} spacing={1.5} sx={{ mt: 1 }}>
              {requests.map((req) => (
                <Stack
                  key={req.id}
                  direction={{ xs: 'column', sm: 'row' }}
                  spacing={1}
                  alignItems={{ sm: 'center' }}
                  justifyContent="space-between"
                >
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {req.target_name}
                    </Typography>
                    <Typography variant="body2">{req.topic}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {dayjs(req.created_at).format('MMM D')}
                      {req.requested_by_email ? ` · ${req.requested_by_email}` : ''}
                      {req.requested_window ? ` · wants: ${req.requested_window}` : ''}
                      {req.status === 'email_sent' ? ' · link sent' : ''}
                    </Typography>
                  </Box>
                  <Stack direction="row" spacing={1}>
                    <Button
                      size="small"
                      variant="contained"
                      disabled={busy}
                      startIcon={<EventAvailableIcon />}
                      onClick={() => doApprove(req.id)}
                    >
                      {req.status === 'email_sent' ? 'Re-send link' : 'Approve & send link'}
                    </Button>
                    <Button
                      size="small"
                      color="warning"
                      disabled={busy}
                      onClick={() => doDecline(req.id)}
                    >
                      Decline
                    </Button>
                  </Stack>
                </Stack>
              ))}
            </Stack>
          )}
        </CardContent>
      </Card>
    </Box>
  );
}


export default StaffMeetingRequestsPage;
